import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaGoogle, FaFacebook } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { EmailLogin } from '../components/auth/EmailLogin';
import { isGoogleOAuthSupported } from '../utils/browser';

const Login: React.FC = () => {
  const { signInWithGoogle, signInWithFacebook } = useAuth();
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState<'social' | 'email'>('social');
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [showCopyMessage, setShowCopyMessage] = useState(false);

  const handleGoogleSignIn = async () => {
    try {
      if (!isGoogleOAuthSupported()) {
        setShowBrowserWarning(true);
        return;
      }
      
      await signInWithGoogle();
      // Redirect will be handled by the auth state change in AuthContext
    } catch (error) {
      console.error('Error signing in with Google:', error);
    }
  };

  const handleFacebookSignIn = async () => {
    try {
      await signInWithFacebook();
      // Redirect will be handled by the auth state change in AuthContext
    } catch (error) {
      console.error('Error signing in with Facebook:', error);
    }
  };

  const handleLoginSuccess = () => {
    navigate('/', { replace: true });
  };

  const handleOpenInBrowser = () => {
    const currentUrl = window.location.href;
    
    // Try to use WeChat's openWithBrowser API
    if (window.wx && typeof window.wx.openInBrowser === 'function') {
      window.wx.openInBrowser({
        url: currentUrl,
        success: function() {
          console.log('Successfully opened in external browser');
        },
        fail: function() {
          // Fallback: Copy URL to clipboard
          copyToClipboard(currentUrl);
        }
      });
    } else {
      // Fallback: Copy URL to clipboard
      copyToClipboard(currentUrl);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowCopyMessage(true);
      setTimeout(() => setShowCopyMessage(false), 3000);
    }).catch(err => {
      console.error('Failed to copy URL:', err);
    });
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-gray-900/50 p-8 rounded-xl border border-white/10">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Sign in to Parlando
          </h2>
        </div>

        {showBrowserWarning && (
          <div className="bg-yellow-900/50 border border-yellow-700 rounded-lg p-4 mb-6">
            <p className="text-yellow-200 text-sm">
              Google sign-in is not supported in the current browser environment. Please use the system browser or try email login instead.
            </p>
            <div className="mt-4 flex justify-end space-x-4">
              <button
                onClick={() => setLoginMethod('email')}
                className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                Use Email Login
              </button>
              <button
                onClick={handleOpenInBrowser}
                className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
              >
                Open in Browser
              </button>
            </div>
            {showCopyMessage && (
              <div className="mt-2 text-sm text-green-400">
                URL copied to clipboard. Please paste it in your system browser.
              </div>
            )}
          </div>
        )}

        <div className="space-y-6">
          {loginMethod === 'social' ? (
            <>
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg 
                        bg-white/10 hover:bg-white/20 border border-white/10 
                        transition-all duration-300"
              >
                <FaGoogle className="text-xl" />
                <span>Continue with Google</span>
              </button>

              <button
                onClick={handleFacebookSignIn}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg 
                        bg-white/10 hover:bg-white/20 border border-white/10 
                        transition-all duration-300"
              >
                <FaFacebook className="text-xl" />
                <span>Continue with Facebook</span>
              </button>

              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-black text-gray-400">
                    Or continue with
                  </span>
                </div>
              </div>

              <button
                onClick={() => setLoginMethod('email')}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg 
                        bg-indigo-600/80 hover:bg-indigo-600 border border-indigo-500/30 
                        transition-all duration-300"
              >
                Email Verification
              </button>
            </>
          ) : (
            <div className="space-y-6">
              <EmailLogin onSuccess={handleLoginSuccess} />
              
              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-black text-gray-400">
                    Or continue with
                  </span>
                </div>
              </div>

              <button
                onClick={() => setLoginMethod('social')}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg 
                        bg-indigo-600/80 hover:bg-indigo-600 border border-indigo-500/30 
                        transition-all duration-300"
              >
                Social Login
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 text-center text-sm text-gray-400">
          <p>By continuing, you agree to our</p>
          <div className="mt-2 space-x-4">
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <span>&middot;</span>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 